/**
 * Système de cache professionnel pour les appels API
 * Gère le cache avec TTL (Time To Live), invalidation intelligente et limite de taille
 */

class ApiCache {
    constructor(options = {}) {
        this.cache = new Map();
        this.maxSize = options.maxSize || 100; // Nombre maximum d'entrées dans le cache
        this.defaultTTL = options.defaultTTL || 5 * 60 * 1000; // 5 minutes par défaut
        this.cleanupInterval = options.cleanupInterval || 60 * 1000; // Nettoyage toutes les minutes
        this.startCleanupTimer();
    }

    /**
     * Génère une clé de cache basée sur l'URL et les paramètres
     */
    generateKey(url, params = {}, method = 'GET') {
        const sortedParams = Object.keys(params)
            .sort()
            .reduce((acc, key) => {
                acc[key] = params[key];
                return acc;
            }, {});
        const paramsString = JSON.stringify(sortedParams);
        return `${method}:${url}:${paramsString}`;
    }

    /**
     * Récupère une valeur du cache si elle existe et n'est pas expirée
     */
    get(key) {
        const entry = this.cache.get(key);

        if (!entry) {
            return null;
        }

        // Vérifier si l'entrée est expirée
        if (Date.now() > entry.expiresAt) {
            this.cache.delete(key);
            return null;
        }

        return entry.data;
    }

    /**
     * Stocke une valeur dans le cache
     */
    set(key, data, ttl = null) {
        // Supprimer les entrées les plus anciennes si le cache est plein
        if (this.cache.size >= this.maxSize) {
            this.evictOldest();
        }

        const ttlValue = ttl || this.defaultTTL;
        const entry = {
            data: data,
            expiresAt: Date.now() + ttlValue,
            createdAt: Date.now(),
        };

        this.cache.set(key, entry);
    }

    /**
     * Supprime une entrée spécifique du cache
     */
    delete(key) {
        this.cache.delete(key);
    }

    /**
     * Supprime toutes les entrées du cache
     */
    clear() {
        this.cache.clear();
    }

    /**
     * Invalide le cache pour un pattern donné (par exemple, toutes les requêtes pour un endpoint)
     */
    invalidate(pattern) {
        const keysToDelete = [];

        for (const key of this.cache.keys()) {
            if (key.includes(pattern)) {
                keysToDelete.push(key);
            }
        }

        keysToDelete.forEach(key => this.cache.delete(key));
    }

    /**
     * Invalide le cache pour une URL de base (par exemple, invalider toutes les requêtes /api/clients/*)
     */
    invalidateByUrl(baseUrl) {
        const keysToDelete = [];

        for (const key of this.cache.keys()) {
            // Vérifier si la clé contient l'URL de base
            if (key.includes(baseUrl)) {
                keysToDelete.push(key);
            }
        }

        keysToDelete.forEach(key => this.cache.delete(key));
    }

    /**
     * Supprime les entrées les plus anciennes du cache
     */
    evictOldest() {
        let oldestKey = null;
        let oldestTime = Infinity;

        for (const [key, entry] of this.cache.entries()) {
            if (entry.createdAt < oldestTime) {
                oldestTime = entry.createdAt;
                oldestKey = key;
            }
        }

        if (oldestKey) {
            this.cache.delete(oldestKey);
        }
    }

    /**
     * Nettoie les entrées expirées
     */
    cleanup() {
        const now = Date.now();
        const keysToDelete = [];

        for (const [key, entry] of this.cache.entries()) {
            if (now > entry.expiresAt) {
                keysToDelete.push(key);
            }
        }

        keysToDelete.forEach(key => this.cache.delete(key));
    }

    /**
     * Démarre le timer de nettoyage automatique
     */
    startCleanupTimer() {
        if (this.cleanupTimer) {
            clearInterval(this.cleanupTimer);
        }

        this.cleanupTimer = setInterval(() => {
            this.cleanup();
        }, this.cleanupInterval);
    }

    /**
     * Arrête le timer de nettoyage
     */
    stopCleanupTimer() {
        if (this.cleanupTimer) {
            clearInterval(this.cleanupTimer);
            this.cleanupTimer = null;
        }
    }

    /**
     * Retourne des statistiques sur le cache
     */
    getStats() {
        const now = Date.now();
        let expiredCount = 0;
        let activeCount = 0;

        for (const entry of this.cache.values()) {
            if (now > entry.expiresAt) {
                expiredCount++;
            } else {
                activeCount++;
            }
        }

        return {
            size: this.cache.size,
            activeEntries: activeCount,
            expiredEntries: expiredCount,
            maxSize: this.maxSize,
        };
    }
}

// Instance singleton du cache
const apiCache = new ApiCache({
    maxSize: 100,
    defaultTTL: 5 * 60 * 1000, // 5 minutes
    cleanupInterval: 60 * 1000, // Nettoyage toutes les minutes
});

export default apiCache;





